import React, { useState } from "react";
import T from "prop-types";
import { get, without } from "lodash";
import {
  ROLES,
  WAREHOUSE,
  WAREHOUSE_LOGISTICS_DIRECTOR
} from "MODULES/users/constants";
import {
  Authorized,
  Icon,
  LocationFlag,
  NoteTooltip as IconTooltip,
  NoteTooltip
} from "MODULES/shared/components";
import {
  ExchangeIcon,
  extCostEvalColumn,
  totalCostEvalColumn,
  totalRepairOrderCost
} from "MODULES/RepairOrders/helpers";
import { translateProps } from "MODULES/shared/decorators";
import { decorators, Paginator } from "@orange-marmalade/paginate-this";
import classNames from "classnames";
import PartNumber from "MODULES/shared/components/PartNumber/PartNumber";
import { Link } from "react-router-dom";
import StatusNote from "MODULES/RepairOrders/components/Detail/LineItems/StatusNote";
import StatusBadges from "MODULES/RepairOrders/components/Detail/LineItems/StatusBadges";
import MultiLocationFeature from "MODULES/shared/components/MultiLocationFeature/MultiLocationFeature";
import HoverFlag from "MODULES/parts/components/HoverFlag";
import { shortDateYY } from "LIB/formatters/date";
import { dollars } from "LIB/formatters/currency";
import RequiredComponentTooltip from "./RequiredComponentTooltip";
import LineItemControls from "./LineItemControls";
import hiddenInputs from "../../../../shared/components/Forms/hiddenInputs";
import Edit from "./Edit";
import {
  ACTIONS_REQUESTED,
  getConditionAbbrev,
  SHOP_ASSIST,
  TEARDOWN
} from "../../../constants";

const hiddenEnhancer = hiddenInputs({
  HiddenTh: props => <th {...props}>{props.children}</th>,
  HiddenTd: props => <td {...props}>{props.children}</td>
});

const Table = props => {
  const clickable = classNames({ clickable: !props.disabled });

  const renderRow = (
    lineItem,
    i,
    HiddenTd,
    HiddenTeardownTd,
    startColumns = {}
  ) => (
    <tr
      key={i}
      className={clickable}
      onClick={() => {
        if (!props.disabled) {
          props.onItemClick(lineItem);
        }
      }}
    >
      <td>{i + 1}</td>
      {Object.keys(startColumns).map(k => {
        const Component = startColumns[k];
        return (
          <td key={k}>
            <Component lineItem={lineItem} />
          </td>
        );
      })}
      <td>
        <PartNumber
          part={{
            id: lineItem.part_id,
            part_number: lineItem.part.part_number,
            uuid: lineItem.part.uuid
          }}
        />
      </td>
      <td>
        {lineItem.inventory ? (
          <Link to={`/inventories/${lineItem.inventory.uuid}`}>
            {lineItem.original_serial_number || lineItem.serial_number}
          </Link>
        ) : (
          lineItem.original_serial_number || lineItem.serial_number
        )}
      </td>
      <HiddenTd>
        <PartNumber part={lineItem.mod_to} />
      </HiddenTd>
      <td>{ACTIONS_REQUESTED[lineItem.action_requested].label}</td>
      <td>
        <StatusNote lineItem={lineItem} />
      </td>
      <HiddenTd className="text-center">
        <StatusBadges lineItem={lineItem} />
        {lineItem.inventory &&
          get(lineItem, "inventory.status") === "sales_order_created" && (
            <NoteTooltip
              placement="right"
              icon="fa-paper-plane"
              note="Pending Sale"
            />
          )}
      </HiddenTd>
      <td>
        <RequiredComponentTooltip lineItem={lineItem} />
      </td>
      <MultiLocationFeature>
        <HiddenTd className="text-center">
          <LocationFlag
            location={lineItem.return_to_location_id}
            unknownFlag={false}
          />
        </HiddenTd>
      </MultiLocationFeature>
      <HiddenTd className="text-success text-center">
        {lineItem.exchange && <ExchangeIcon lineItem={lineItem} />}
      </HiddenTd>
      <td>{lineItem.work_order_number}</td>
      <td>
        {getConditionAbbrev(lineItem.condition_sent)}
        {get(lineItem, "inventory.as_is") && (
          <HoverFlag text={"As-is"} abbr="AI" inline />
        )}
      </td>
      <td>{getConditionAbbrev(get(lineItem, "inventory.condition"))}</td>
      <td className="text-center">{shortDateYY(lineItem.eval_due_date)}</td>
      <td className="text-center">{shortDateYY(lineItem.expected_date)}</td>
      <td className="text-right">{lineItem.quantity}</td>
      <td className="text-right">
        {lineItem.received && (
          <span className="text-muted spacing-right">
            <Icon name="cloud-download" />{" "}
          </span>
        )}
        {lineItem.quantity_received}
      </td>
      <HiddenTeardownTd className="text-right">
        {dollars(lineItem.transfer_cost * Number(lineItem.quantity))}
      </HiddenTeardownTd>
      <td className="text-right">{totalCostEvalColumn(lineItem)}</td>
      <td className="text-right">{extCostEvalColumn(lineItem)}</td>
      <td>
        {(lineItem.internal_note || lineItem.external_note) && (
          <NoteTooltip
            note={[lineItem.internal_note, lineItem.external_note]
              .filter(note => note)
              .join(" | ")}
          />
        )}
      </td>
      {!lineItem.scrap_report && (
        <LineItemControls
          lineItem={lineItem}
          returnItemModalOpen={props.returnItemModalOpen}
          toggleReturnModal={() => props.toggleReturnItemModal(lineItem)}
        />
      )}
    </tr>
  );

  const { results, startColumns = {}, endColumns = {} } = props;
  const { HiddenTd, HiddenTh } = hiddenEnhancer(
    results.some(r => r.action_requested === SHOP_ASSIST)
  );

  const {
    HiddenTd: HiddenTeardownTd,
    HiddenTh: HiddenTeardownTh
  } = hiddenEnhancer(!results.some(r => r.action_requested === TEARDOWN));

  return (
    <table className="table table-striped table-hover table-numbered">
      <thead>
        <tr>
          <th />
          <th />
          <th>Part #</th>
          <th>Serial #</th>
          <HiddenTh>Mod To</HiddenTh>
          <th>Action Req.</th>
          <th />
          <HiddenTh className="text-center">Eval Status</HiddenTh>
          <th />
          <MultiLocationFeature>
            <HiddenTh className="text-center">Return Store</HiddenTh>
          </MultiLocationFeature>
          <HiddenTh className="text-center">Exch</HiddenTh>
          <th>WO #</th>
          <th>CD Sent</th>
          <th>Inv CD</th>
          <th>Eval Due</th>
          <th className="text-center">Expected</th>
          <th className="text-right">QTY</th>
          <th className="text-right">Rec.</th>
          <HiddenTeardownTh className="text-right">
            Transfer Cost
          </HiddenTeardownTh>
          <th className="text-right">Total Cost/Eval</th>
          <th className="text-right">Ext Cost/Eval</th>
          <th />
          <th />
        </tr>
      </thead>
      <tbody>
        {results.map((r, i) =>
          renderRow(r, i, HiddenTd, HiddenTeardownTd, startColumns, endColumns)
        )}
        <tr style={{ backgroundColor: "transparent" }}>
          <td />
          <td className="text-right" colSpan={20}>
            <h4 className="margin-top-none margin-bottom-none">
              Total: {totalRepairOrderCost(results)}
            </h4>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const DefaultRepairOrderTable = props => {
  const [returnItemModalOpen, setReturnItemModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [lineItem, setLineItem] = useState({});

  const openModal = l => {
    setModalOpen(true);
    setLineItem(l);
  };

  const closeModal = () => setModalOpen(false);

  const toggleReturnItemModal = item => {
    setReturnItemModalOpen(returnItemModalOpen === item.id ? false : item.id);
  };

  const { repairOrder, results } = props;

  return (
    <React.Fragment>
      <Table
        results={results}
        onItemClick={openModal}
        returnItemModalOpen={returnItemModalOpen}
        toggleReturnItemModal={toggleReturnItemModal}
        startColumns={{
          "": ({ lineItem: item }) => (
            <td>
              {item.exchange && (
                <IconTooltip
                  icon="fa-exchange"
                  placement="top"
                  note="Core Return"
                  color="#FFFFFF"
                />
              )}
            </td>
          )
        }}
      />
      <Authorized
        roles={without(ROLES, WAREHOUSE)}
        subroles={WAREHOUSE_LOGISTICS_DIRECTOR}
      >
        <Edit
          open={modalOpen}
          close={closeModal}
          repairOrder={repairOrder}
          lineItem={lineItem}
          openReturnModal={toggleReturnItemModal}
        />
      </Authorized>
      <nav
        key="pagination"
        className="text-center margin-top-none"
        aria-label="Search results pages"
      >
        <Paginator
          listId="repairOrders/lineItems"
          lineItems={results}
          repairOrder={repairOrder}
        />
      </nav>
    </React.Fragment>
  );
};

DefaultRepairOrderTable.propTypes = {
  repairOrder: T.instanceOf(Map),
  lineItem: T.shape({})
};

const withListId = translateProps(() => ({
  listId: "repairOrders/lineItems"
}));

export default withListId(decorators.tabulate(DefaultRepairOrderTable));



// WEBPACK FOOTER //
// ./app/modules/RepairOrders/components/Detail/LineItems/DefaultRepairOrderTable.jsx